import { useTranslation } from 'react-i18next'
import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useFeatureFlags } from '@fs/zion-flags'
import { Button, Alert, useOverlay, Paragraph, FlowGrid, H6 } from '@fs/zion-ui'
import { ContentAdd } from '@fs/zion-icon'
import ZionForm, { useFieldArray, useZionForm, useWatch, FormAutosuggest, useFormState } from '@fs/zion-form'
import {
  generateSls,
  saveSlsAndUpdateState,
  useSlsStateContext,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  getPersonName,
} from '@fs/zion-sls'
import { yupResolver } from '@hookform/resolvers/yup'
import useFamilyReviewRelationshipOptions from '../../../views/familyReview/hooks/useFamilyReviewRelationshipOptions'
import {
  prepareFamilyMembersDefaultValues,
  prepareFamilyMemberNamesAutosuggest,
  getGroupedGenderedRelTypeSuggestions,
  getPersonIdsToReviewFromCreateActions,
  getHouseholdSuggestions,
  getPersonsAndRelationshipsForHousehold,
  prepareRelationshipsFields,
  getPersonIdsPendingReview,
  checkIfHouseholdNeedsPrimaryPerson,
  NO_RELATION_SUGGESTION,
} from './utils/familyMember.utils'
import { updateRelationshipType } from './utils/relationshipMap.utils'
import { generateDispatchesFromRelationshipsMap } from './utils/generateDispatchesFromRelationshipsMap'
import CardFooter from '../shared/components/CardFooter'
import PreviousRelationshipDialogOverlay from './components/PreviousRelationshipDialogOverlay'
import PrimaryPersonDialogOverlay from './components/PrimaryPersonDialogOverlay'
import getRelationshipDispatches, { MAKE_NEW_RECORD } from './utils/saveRelationships'
import { FullTextApp, headers } from '../../../../shared/services/config'
import { errorCollector } from '../../../../shared/utils/utils'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'
import { collectHighlightIdsForRelationships } from '../../../views/indexing/components/editPanels/editRelationships/utils/EditRelationships.utls'
import { householdDetailsValidationSchema } from './utils/validationSchema'
import RelationshipInputs from './components/RelationshipInputs'
import useRelationshipsMap from './hooks/useRelationshipsMap'

/**
 * FamilyMembersCardEditMode
 *
 * Provides an editable form interface for managing a person's family member relationships.
 * Each relationship row supports selecting a relationship type and a related person's name
 * (with autosuggest). New rows can be added; existing rows removed. The form integrates with
 * SLS (Shared Linked State) actions to persist relationship changes.
 *
 * Behavior & Flow:
 *  - Default form values are prepared from the incoming `relationships` prop.
 *  - On submit, differences are calculated and converted into SLS dispatches which are saved
 *    via `saveSlsAndUpdateState`. Errors are captured and surfaced with an Alert.
 *  - If the card has not yet been reviewed, submitting marks it reviewed via `setReviewed(true)`.
 *
 * @component
 * @param {Object}   props
 * @param {Object}   props.person                       The primary person SLS person object (must include `id` and `recordId`).
 * @param {Array}    props.allRecords                   Array of all RECORD type SLS objects in the current image.
 * @param {Array}    props.relationships                Array of existing relationship objects used for defaults.
 * @param {boolean}  [props.reviewed=false]             Whether the relationships section has been reviewed.
 * @param {string}   [props.personGender]               Gender of the primary person (used for relationship suggestions).
 * @param {Function} props.setReviewed                  Callback to set reviewed state in parent.
 * @param {Function} props.setIsEditing                 Toggles the parent card edit mode off after save / cancel.
 * @param {string}   props.userId                       Current user id for audit purposes passed to save.
 * @param {Function} [props.onSave]                     Callback invoked after successful save with form data.
 * @param {Function} [props.onDirtyChange=(isDirty) => {}] Callback function invoked when the form dirty state changes.
 * @param {boolean}  [props.saveCardDataToSls=false]    Whether to save changes to SLS backend.
 * @param {string}   [props.appName=FullTextApp]        The application name for SLS operations.
 * @param {string}   [props.splitScreenPartitionKey]        Split screen partition key if using split screen.
 *
 * @returns {JSX.Element}
 */
const FamilyMembersCardEditMode = ({
  person,
  allRecords,
  relationships,
  reviewed = false,
  personGender,
  setReviewed,
  setIsEditing,
  userId,
  onSave = () => {},
  onDirtyChange = () => {},
  saveCardDataToSls = false,
  splitScreenPartitionKey,
  appName = FullTextApp,
}) => {
  const [t] = useTranslation()
  const { types, images, currentImageArk } = useSlsStateContext()
  const h = useSlsHighlightsContext()
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
  } = useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_sls_actions_splunk_debugger'])

  const householdSuggestions = useMemo(() => {
    return getHouseholdSuggestions({ records: allRecords, person })
  }, [allRecords, person])

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const slsDispatch = useSlsDispatchContext()
  const nameAutosuggestRefs = useRef([])
  const previousRelationshipOverlay = useOverlay()
  const primaryPersonOverlay = useOverlay()
  const { topMostPanel } = useOverlayContext()
  const { setDisplayHighlights, getHighlights } = useSlsHighlightsContext()
  const [personsToReview, setPersonsToReview] = useState([])
  const [currentReviewPersonIndex, setCurrentReviewPersonIndex] = useState(0)

  const relationshipSuggestions = useMemo(() => {
    return getGroupedGenderedRelTypeSuggestions(types, personGender, true)
  }, [types, personGender])

  const defaultValues = useMemo(() => {
    return prepareFamilyMembersDefaultValues(relationships, person, householdSuggestions)
  }, [relationships, person, householdSuggestions])

  // Custom hook to manage relationships map, keeps track of existing relationships
  const relationshipsMapApi = useRelationshipsMap()

  const formMethods = useZionForm({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(householdDetailsValidationSchema),
  })

  const fieldArrayMethods = useFieldArray({
    name: 'relationships',
    control: formMethods.control,
  })

  const { fields, append } = fieldArrayMethods

  const watchedRelationships = useWatch({
    control: formMethods.control,
    name: 'relationships',
  })

  const watchedFormValues = useWatch({
    control: formMethods.control,
  })

  const imageNamesAutoCompleteSuggestions = useFamilyReviewRelationshipOptions({
    images,
    mainPartitionKey: currentImageArk,
    splitScreenPartitionKey,
    primaryPersonId: person?.id,
    currentRecordId: watchedFormValues.targetRecordId || person?.recordId,
    includeRecordId: true,
  })

  const namesAutoSuggestions = prepareFamilyMemberNamesAutosuggest({
    relationships,
    namesAutoSuggest: imageNamesAutoCompleteSuggestions,
  })

  // Track form dirty state to inform parent component so it can trigger unsaved changes overlay
  const { isDirty } = useFormState({ control: formMethods.control })

  const topMostPanelId = useMemo(() => topMostPanel(), [topMostPanel])

  // Removed isButtonDisabled logic to comply with RIP-4674:
  // Buttons should never be greyed out based on validation.
  // Validation errors will be shown when the user clicks Save/Next.

  const handleSuccess = useCallback(() => {
    if (!reviewed) {
      setReviewed(true)
    }
    setIsEditing(false)
    setSaving(false)
  }, [reviewed, setReviewed, setIsEditing])

  const saveDispatches = useCallback(
    async (data, reviewDispatches) => {
      setSaving(true)
      setError(null)

      try {
        // Generate dispatches - this will throw if validation fails
        // Pass all form data including recordGroup for cross-record handling
        const newPersonAndRelationshipDispatches = await getRelationshipDispatches({
          formData: data,
          defaultValues,
          person,
          images,
          appName,
          reviewDispatches,
          currentPartitionKey: currentImageArk,
        })

        // Get highlight dispatches from the highlights context
        const highlightDispatches = h.getDispatches({
          mainImageArk: currentImageArk,
          splitScreenImageArk: splitScreenPartitionKey,
        })

        // Combine all dispatches
        const allDispatches = [...newPersonAndRelationshipDispatches, ...highlightDispatches]

        const personIdsToReview = getPersonIdsToReviewFromCreateActions(allDispatches)
        const sls = generateSls(allDispatches)

        if (saveCardDataToSls) {
          saveSlsAndUpdateState({
            sls,
            dispatches: allDispatches,
            saveChangesFlag,
            slsActionsSplunkDebugger,
            userId,
            dispatch: slsDispatch,
            appName,
            fsUserAgentChain: headers['FS-User-Agent-Chain'],
            errorHandler: (err) => {
              setError(err)
              errorCollector(err, null, {
                codeId: 90,
                sls,
                status: err?.response?.status,
                responseBody: err?.response?.data,
              })
              setSaving(false)
            },
            successHandler: () => {
              // Processing SLS dispatches to update local state even if the saveChangesFlag is off
              if (!saveChangesFlag.isOn) {
                slsDispatch({
                  type: 'BATCH_DISPATCHES',
                  value: newPersonAndRelationshipDispatches,
                })
              }

              setSaving(false)
              // The AddNamesPanel needs personIdsToReview to add newly created people to the review list.
              onSave({
                formValues: data,
                personIdsToReview,
              })
              h.commitEdits()
            },
            saveInitiatorFilename: 'FamilyMembersCardEditMode.js',
          })
        } else {
          setSaving(false)
          onSave({
            dispatches: allDispatches,
            sls,
            formValues: data,
            personIdsToReview,
          })
          h.commitEdits()
        }
      } catch (err) {
        // Validation errors (duplicate relationships, missing data, etc.)
        // eslint-disable-next-line no-console
        setError(err)
        errorCollector(err, null, {
          codeId: 91,
          message: 'Relationship validation error',
        })
        setSaving(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      appName,
      defaultValues,
      handleSuccess,
      images,
      onSave,
      saveCardDataToSls,
      saveChangesFlag,
      slsActionsSplunkDebugger,
      person,
      slsDispatch,
      userId,
      currentImageArk,
      splitScreenPartitionKey,
    ]
  )

  const onSubmit = useCallback(
    async (data) => {
      // Ensure household has a value - default to MAKE_NEW_RECORD if cleared
      if (!data?.household?.key) {
        const defaultHousehold = householdSuggestions.find((hh) => hh.key === MAKE_NEW_RECORD)
        if (defaultHousehold) {
          data = { ...data, household: defaultHousehold, targetRecordId: defaultHousehold.key }
          formMethods.setValue('household', defaultHousehold, { shouldDirty: true })
          formMethods.setValue('targetRecordId', defaultHousehold.key, { shouldDirty: true })
        }
      }

      // Check if person is floating (needs to be attached to a record)
      // Floating persons don't have a recordId and need to be saved even if the form is pristine
      const isFloatingPerson = !person?.recordId

      // Only proceed if form is dirty OR person is floating
      if (isDirty || isFloatingPerson) {
        // Check if we need to select a primary person
        const needsPrimaryPerson = checkIfHouseholdNeedsPrimaryPerson({
          formData: data,
          images,
          person,
        })

        if (needsPrimaryPerson) {
          primaryPersonOverlay.open()
          return // Stop here, will continue after primary person is selected
        }

        // Auto-assign primary for single-person household
        const activeRelationships = data.relationships.filter((rel) => !rel.isDeleted && rel.personId)
        if (activeRelationships.length === 0 && !data.targetPersonIsPrimary) {
          formMethods.setValue('targetPersonIsPrimary', true, { shouldDirty: true })
          data = { ...data, targetPersonIsPrimary: true }
        }

        // Check if there are any relationships that need review for new persons being added to the household
        const { newMemberIds, newMemberIdsWithExistingRelationships } = getPersonIdsPendingReview(data, images)

        // We show the previous relationship overlay only for persons that have existing relationships
        // or to give them a chance to add relationships to the other household members, so we filter accordingly
        const atLeastTwoPersonsInRecord = data.relationships.filter((rel) => !rel.isDeleted).length >= 2

        if (atLeastTwoPersonsInRecord && newMemberIds.length > 0) {
          // If there are at least two persons in the household after the changes
          // we review all of them to give them a chance to add relationships to each other
          setPersonsToReview(newMemberIds)
          previousRelationshipOverlay.open()
        } else if (newMemberIdsWithExistingRelationships.length > 0) {
          // Otherwise we only review those with existing relationships to inform them about removals
          setPersonsToReview(newMemberIdsWithExistingRelationships)
          previousRelationshipOverlay.open()
        } else {
          setPersonsToReview([])
          await saveDispatches(data, [])
        }
      } else {
        setSaving(false)
        onSave({
          formValues: data,
        })
        h.commitEdits()
      }
    },
    [
      person,
      isDirty,
      images,
      formMethods,
      primaryPersonOverlay,
      previousRelationshipOverlay,
      setPersonsToReview,
      saveDispatches,
      setSaving,
      onSave,
      h,
      householdSuggestions,
    ]
  )

  const handleCancel = useCallback(() => {
    formMethods.reset()
    onDirtyChange(false)
    setIsEditing(false)
  }, [formMethods, setIsEditing, onDirtyChange])

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    if (topMostPanelId !== 'highlightsPanel' && currentImageArk) {
      const highlights = getHighlights()
      const { allPeopleHighlights, allPeopleReferenceHighlights } = collectHighlightIdsForRelationships(
        watchedFormValues,
        person.id,
        highlights.mainEditHighlights,
        highlights.splitScreenEditHighlights
      )

      setDisplayHighlights({
        highlights: [...allPeopleHighlights, ...allPeopleReferenceHighlights],
        mainPartitionKey: currentImageArk,
        splitScreenPartitionKey,
        fieldTypes: ['NAME_SURN', 'NAME_SUF', 'NAME_GN'],
        customFilter: (highlight) => highlight.personIds.length > 0,
      })
    }
  }, [
    currentImageArk,
    splitScreenPartitionKey,
    watchedFormValues,
    person.id,
    topMostPanelId,
    setDisplayHighlights,
    getHighlights,
  ])

  // Handle saving from the previous relationship overlay
  const handleReviewSave = useCallback(async () => {
    // Move to next person to review or finish
    previousRelationshipOverlay.close()
    if (currentReviewPersonIndex + 1 < personsToReview.length) {
      setCurrentReviewPersonIndex(currentReviewPersonIndex + 1)
      previousRelationshipOverlay.open()
    } else {
      // All done with reviews - generate dispatches from the relationships map
      // The map is the single source of truth and naturally prevents duplicates
      const reviewDispatches = generateDispatchesFromRelationshipsMap({
        relationshipsMapApi,
        images,
        currentImageArk,
        mainPersonId: person.id,
        targetRecordId: watchedFormValues.targetRecordId,
      })

      await saveDispatches(formMethods.getValues(), reviewDispatches)

      // Reset review state
      setPersonsToReview([])
      setCurrentReviewPersonIndex(0)
    }
  }, [
    currentReviewPersonIndex,
    previousRelationshipOverlay,
    personsToReview.length,
    relationshipsMapApi,
    images,
    currentImageArk,
    person.id,
    watchedFormValues.targetRecordId,
    formMethods,
    saveDispatches,
  ])

  // Handle saving from the primary person overlay
  const handlePrimaryPersonSave = useCallback(
    async (primaryPersonIds) => {
      primaryPersonOverlay.close()

      // Update form data with ALL selected primary persons
      const formRelationships = formMethods.getValues('relationships')

      // Check if main person is in the selection
      if (primaryPersonIds.includes(person.id)) {
        formMethods.setValue('targetPersonIsPrimary', true, { shouldDirty: true })
      } else {
        formMethods.setValue('targetPersonIsPrimary', false, { shouldDirty: true })
      }

      // Update each relationship based on selection
      formRelationships.forEach((rel, index) => {
        if (!rel.isDeleted) {
          // Use personId as identifier (works for both existing and NEW_PERSON synthetic IDs)
          const identifier = rel.personId
          if (identifier) {
            const shouldBePrimary = primaryPersonIds.includes(identifier)
            formMethods.setValue(`relationships.${index}.secondaryPersonPrimaryStatusChange`, shouldBePrimary, {
              shouldDirty: true,
            })
          }
        }
      })

      // Re-submit the form to continue the save flow
      formMethods.handleSubmit(onSubmit)()
    },
    [primaryPersonOverlay, person.id, formMethods, onSubmit]
  )

  // Current person ID being reviewed
  const currentPersonIdToReview = useMemo(() => {
    if (personsToReview.length > 0 && currentReviewPersonIndex < personsToReview.length) {
      return personsToReview[currentReviewPersonIndex]
    }
    return null
  }, [personsToReview, currentReviewPersonIndex])

  const handleRelationshipChangeInPreviousOverlay = useCallback(
    (suggestion, field) => {
      updateRelationshipType({
        relationshipsMapApi,
        suggestion,
        field,
        firstPersonId: field.personId,
        secondPersonId: currentPersonIdToReview,
      })
    },
    [currentPersonIdToReview, relationshipsMapApi]
  )

  // Whether to show the previous relationship overlay
  const showPreviousRelationshipOverlay = useMemo(() => {
    return (previousRelationshipOverlay.isOpen || previousRelationshipOverlay.transitioning) && currentPersonIdToReview
  }, [previousRelationshipOverlay.isOpen, previousRelationshipOverlay.transitioning, currentPersonIdToReview])

  return (
    <>
      <ZionForm methods={formMethods} onSubmit={onSubmit} id="household-card-form">
        <FlowGrid orientation="vertical" columnCount={1} gutters="xs">
          <Paragraph size="sm">
            {t('household-details-card.instruction', 'Create a new household, or include with an existing one?')}
          </Paragraph>

          <FormAutosuggest
            name="household"
            label={t('household-details-card.household-label', 'Household')}
            suggestions={householdSuggestions}
            onSuggestionSelected={(suggestion) => {
              const { relationships: householdRelationships } = getPersonsAndRelationshipsForHousehold({
                person,
                images,
                allRecords,
                recordId: suggestion?.key,
                currentPartitionKey: currentImageArk,
              })

              const { relationships: newHouseholdMembers, relationshipsById: newHouseholdMembersById } =
                prepareRelationshipsFields(householdRelationships)

              formMethods.setValue('targetRecordId', suggestion?.key, { shouldDirty: true })
              formMethods.setValue('relationships', newHouseholdMembers, { shouldDirty: true })
              formMethods.setValue('relationshipsById', newHouseholdMembersById, { shouldDirty: true })
            }}
            clearable
            dense
          />

          <FlowGrid gutters="pico" orientation="vertical" columnCount={1}>
            <H6>{t('household-details-card.household-member-relations', 'Household Relationships')}</H6>
            <Paragraph size="sm">
              {t(
                'household-details-card.relationships-description',
                'Add individuals to this household and indicate how they are related to {personName}.',
                { personName: getPersonName({ person }) }
              )}
            </Paragraph>
          </FlowGrid>

          {fields.map((field, index) => (
            <RelationshipInputs
              key={field?.id}
              field={field}
              index={index}
              relationshipSuggestions={relationshipSuggestions}
              namesAutoSuggestions={namesAutoSuggestions}
              formMethods={formMethods}
              fieldArrayMethods={fieldArrayMethods}
              refs={nameAutosuggestRefs}
            />
          ))}

          <Button
            Icon={ContentAdd}
            onClick={() => {
              const newFormId = `rel_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`

              append({
                formId: newFormId,
                slsRelationshipId: null,
                personId: null,
                relationshipType: NO_RELATION_SUGGESTION.key,
                relationshipDirection: null,
                relationship: NO_RELATION_SUGGESTION,
                value: null,
                isNew: true,
                isDeleted: false,
                secondaryPersonPrimaryStatusChange: null,
              })
            }}
            data-testid="add-household-member-button"
            linkName="household-card-add-household-member"
          >
            {t('household-details-card.household-member.btn', 'Household Member')}
          </Button>

          <CardFooter reviewed={reviewed} onCancel={handleCancel} saving={saving} label="family-members" />
          {error && (
            <Alert type="error">
              {t('household-details-card.save-error-msg', 'Error saving data. Please try again later.')}
            </Alert>
          )}
        </FlowGrid>
      </ZionForm>
      {showPreviousRelationshipOverlay && currentPersonIdToReview && (
        <PreviousRelationshipDialogOverlay
          key={currentPersonIdToReview}
          overlay={previousRelationshipOverlay}
          personIdToReview={currentPersonIdToReview}
          targetRecordId={watchedFormValues.targetRecordId}
          allRecords={allRecords}
          currentFocusedPersonId={person?.id}
          updatedRelationships={watchedRelationships}
          relationshipSuggestions={relationshipSuggestions}
          relationshipsMapApi={relationshipsMapApi}
          onRelationshipChange={handleRelationshipChangeInPreviousOverlay}
          onSave={handleReviewSave}
          onCancel={() => {
            previousRelationshipOverlay.close()
            setPersonsToReview([])
            setCurrentReviewPersonIndex(0)
          }}
        />
      )}
      {(primaryPersonOverlay.isOpen || primaryPersonOverlay.transitioning) && (
        <PrimaryPersonDialogOverlay
          overlay={primaryPersonOverlay}
          householdMembers={watchedRelationships}
          currentFocusedPersonId={person?.id}
          currentFocusedPersonName={getPersonName({ person })}
          onSave={handlePrimaryPersonSave}
          onCancel={() => {
            // Reset saving state when user cancels
            setSaving(false)
            // Dialog closure is handled by PrimaryPersonDialogOverlay
          }}
        />
      )}
    </>
  )
}

FamilyMembersCardEditMode.propTypes = {
  person: PropTypes.shape({
    // Required properties
    id: PropTypes.string.isRequired,
    // recordId can be null for floating persons (persons not yet attached to a record)
    recordId: PropTypes.string,
  }),

  relationships: PropTypes.arrayOf(
    PropTypes.shape({
      relKey: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.oneOf([null])]),
      otherPersonId: PropTypes.string.isRequired,
    })
  ),

  reviewed: PropTypes.bool,
  personGender: PropTypes.string,
  saveCardDataToSls: PropTypes.bool,
  splitScreenPartitionKey: PropTypes.string,
}

FamilyMembersCardEditMode.defaultProps = {
  person: { id: '', recordId: null },
  relationships: [],
  reviewed: false,
  personGender: null,
  saveCardDataToSls: false,
}

export default FamilyMembersCardEditMode
